import { useEffect, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Code, Group, Stack, Table, Text, TextInput, Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconArrowLeft, IconEye, IconTrash } from '@tabler/icons-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { modals } from '@mantine/modals';

import {
  useDeleteSegment, useEventNames, usePreviewSegment, useSaveSegment, useSegment, useSegments,
} from '../api/hooks';
import { EMPTY_DEFINITION, SegmentBuilder } from '../components/SegmentBuilder';
import { PageHeader } from '../components/PageHeader';
import { ApiRequestError } from '../api/client';
import { fmtNumber } from '../theme/format';
import type { SegmentNode } from '../api/types';

export function SegmentEditorPage() {
  const { id = 'new' } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const { data: existing } = useSegment(id);
  const { data: eventNames } = useEventNames();
  const { data: allSegments } = useSegments();
  const save = useSaveSegment();
  const remove = useDeleteSegment();
  const preview = usePreviewSegment();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [definition, setDefinition] = useState<SegmentNode>(EMPTY_DEFINITION);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setDescription(existing.description);
      setDefinition(existing.definition);
    }
  }, [existing]);

  function runPreview() {
    setError(null);
    preview.mutate(definition, {
      onError: (e) =>
        setError(
          e instanceof ApiRequestError
            ? [e.message, ...Object.values(e.fieldErrors())].join(' — ')
            : 'Preview failed',
        ),
    });
  }

  function submit() {
    setError(null);
    save.mutate(
      { name, description, definition, kind: 'computed' },
      {
        onSuccess: (s) => {
          notifications.show({ message: `Saved ${s.name}`, color: 'teal' });
          navigate(`/segments/${s.id}`);
        },
        onError: (e) =>
          setError(
            e instanceof ApiRequestError
              ? [e.message, ...Object.values(e.fieldErrors())].join(' — ')
              : 'Save failed',
          ),
      },
    );
  }

  return (
    <>
      <PageHeader
        title={isNew ? 'New segment' : name || 'Segment'}
        description="Preview before you save. A count alone cannot tell you whether the predicate found the RIGHT people."
        action={
          <Group gap="xs">
            <Anchor component={Link} to="/segments" size="sm">
              <Group gap={4}><IconArrowLeft size={14} /> All segments</Group>
            </Anchor>
            {!isNew && (
              <Button
                variant="subtle" color="red" size="xs"
                leftSection={<IconTrash size={14} />}
                onClick={() =>
                  modals.openConfirmModal({
                    title: 'Delete this segment?',
                    children: (
                      <Text size="sm">
                        Journeys and scheduled sends that reference it will fail with a
                        clear error rather than silently sending to nobody.
                      </Text>
                    ),
                    labels: { confirm: 'Delete', cancel: 'Keep' },
                    confirmProps: { color: 'red' },
                    onConfirm: () =>
                      remove.mutate(id, { onSuccess: () => navigate('/segments') }),
                  })
                }
              >
                Delete
              </Button>
            )}
          </Group>
        }
      />

      <Stack gap="lg">
        <Card>
          <Stack>
            <Group grow align="flex-start">
              <TextInput
                label="Name"
                placeholder="Lapsed pro users"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <Textarea
                label="Description"
                placeholder="Pro plan, bought in the last 30 days, has not opened the app in a week"
                autosize minRows={1}
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
              />
            </Group>
          </Stack>
        </Card>

        <Card>
          <Text fw={500} mb="md">Conditions</Text>
          <SegmentBuilder
            value={definition}
            onChange={setDefinition}
            eventNames={eventNames ?? []}
            segments={(allSegments ?? []).filter((s) => s.id !== id)}
          />
        </Card>

        {error && (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            {error}
          </Alert>
        )}

        <Group>
          <Button
            variant="light"
            leftSection={<IconEye size={16} />}
            loading={preview.isPending}
            onClick={runPreview}
          >
            Preview
          </Button>
          <Button loading={save.isPending} disabled={!name} onClick={submit}>
            {isNew ? 'Create segment' : 'Save changes'}
          </Button>
        </Group>

        {preview.data && (
          <Card>
            <Group justify="space-between" mb="md">
              <Group gap="xs">
                <Text fw={500}>Preview</Text>
                <Badge size="lg" variant="light">
                  {fmtNumber(preview.data.count)} profiles
                </Badge>
              </Group>
              <Text size="xs" c="dimmed" ff="monospace">{preview.data.summary}</Text>
            </Group>

            {preview.data.sample.length === 0 ? (
              <Text size="sm" c="dimmed">
                Nothing matched. Check the trait names and the time windows — a trait
                that does not exist matches nobody rather than erroring.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={600}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>External ID</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Phone</Table.Th>
                      <Table.Th>Traits</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {preview.data.sample.map((p) => (
                      <Table.Tr key={p.id}>
                        <Table.Td>
                          <Anchor component={Link} to={`/profiles/${p.id}`} size="sm">
                            {p.external_id ?? p.id.slice(0, 8)}
                          </Anchor>
                        </Table.Td>
                        <Table.Td><Text size="sm">{p.email ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="sm">{p.phone ?? '—'}</Text></Table.Td>
                        <Table.Td>
                          <Code style={{ fontSize: 11 }}>
                            {JSON.stringify(p.traits).slice(0, 80)}
                          </Code>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Card>
        )}
      </Stack>
    </>
  );
}
